import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import {GlobalNotificationDTO} from "@/types/GlobalNotificationDTO";
import { handleApiError } from "@/utils/apiErrorHandler";
import { clearError } from "./errorSlice";
import { CONTENT_ENDPOINTS } from "@/store/api/endpoints";



export type AlertType = "STANDARD" | "URGENT" | "SUCCESS" | string;

// Define alert state with proper typing
export interface AlertsState {
  alertData: GlobalNotificationDTO[]; // Merged data
  temporary: GlobalNotificationDTO[];
  loading: boolean;
  error: string | null | undefined;
}

// Async thunk for fetching alerts with proper error handling
export const fetchAlerts = createAsyncThunk(
  "alerts/fetchAlerts",
  async (
    { filterDisplay }: { filterDisplay: "preLogin" | "postLogin" | "both" },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(clearError());
      const url =
        filterDisplay === "preLogin"
          ? `${CONTENT_ENDPOINTS.ALERTS}?filterDisplay=preLogin`
          : `${CONTENT_ENDPOINTS.SECURE_ALERTS}?filterDisplay=postLogin`;
      const response = await axios.get(url);
      const alerts = response.data;
      const formattedAlerts = alerts.map((alert: GlobalNotificationDTO) => ({
        ...alert,
        visible: true,
        persist: true,
      }));
      return formattedAlerts as GlobalNotificationDTO[];
    } catch (error) {
      handleApiError(error as AxiosError, "alerts", dispatch);
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch alerts"
      );
    }
  }
);

// Initial state
const initialState: AlertsState = {
  alertData: [], // Combined data
  temporary: [],
  loading: false,
  error: null as string | null,
};

// Alert slice with properly typed actions
const alertsSlice = createSlice({
  name: "alerts",
  initialState,
  reducers: {
    addAlert: (
      state,
      action: PayloadAction<GlobalNotificationDTO & { tempAlertType?: string }>
    ) => {
      const { tempAlertType, ...alert } = action.payload;
      if (tempAlertType) {
        state.temporary.push(alert);
      } else {
        state.alertData.push(alert); // Push to alertData
      }
    },
    hideAlert: (state, action: PayloadAction<string | number>) => {
      const alertId = action.payload;
      // Update visibility in all alert collections
      state.alertData = state.alertData.map((alert) =>
        alert.id === alertId ? { ...alert, visible: false } : alert
      );
    },
    removeTempAlert: (state, action: PayloadAction<string | number>) => {
      state.temporary = state.temporary.filter(
        (alert) => alert.id !== action.payload
      );
    },
    clearAllAlerts: (state) => {
      state.alertData = [];
      state.temporary = [];
    },
    resetAlertError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alertData = action.payload; // Set alertData
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "An error occurred fetching alerts";
      });
  },
});

export const {
  addAlert,
  hideAlert,
  removeTempAlert,
  clearAllAlerts,
  resetAlertError,
} = alertsSlice.actions;

export default alertsSlice.reducer;
